import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'
import { BookmarkCategory } from '../api/bookmark'
import { toastUtil } from '../utils/toast'

/** 리스트 커버 한 변(px) — 정사각 */
export const COVER_SIZE = 60

/** 목록에 한 번에 보일 리스트 수 */
const VISIBLE_ROWS = 4
/** 한 행 높이 = 커버(60) + 상하 패딩(12*2). 실측 84px과 일치 */
const ROW_HEIGHT = COVER_SIZE + 24
/**
 * 목록 영역의 최대 높이. 딱 VISIBLE_ROWS 만큼만 보이고 그 다음 행은 걸치지 않는다.
 *
 * 시트 전체에 고정 높이를 주는 대신 목록만 제한한다. 머리말(장소명·주소)은 주소가
 * 없거나 이름이 길어 줄바꿈되면 높이가 달라지는데, 시트를 고정하면 그만큼 목록이
 * 늘거나 줄어 다음 행이 삐져나온다.
 */
const LIST_MAX_HEIGHT = ROW_HEIGHT * VISIBLE_ROWS

/**
 * 리스트를 누른 뒤 이만큼 조용하면 시트가 알아서 닫힌다.
 *
 * 바로 닫으면 두 번째 리스트를 고를 수가 없다 — 여러 곳에 담는 건 되는 일이다.
 * 그렇다고 계속 열어두면 한 곳만 담는 대부분의 경우에 쓸데없이 한 번 더 닫게
 * 한다. 그래서 "손을 뗐나" 로 판단한다. 시트를 다시 건드리면 취소되고 처음부터
 * 다시 센다(cancelAutoClose).
 */
const AUTO_CLOSE_MS = 1000

/** 올라오는 시간(ms) */
const ENTER_MS = 260
/**
 * 내려가는 시간(ms).
 *
 * 올라올 때보다 조금 빠르다. 열릴 때는 "여기 있다" 를 보여주는 동작이라
 * 여유가 있어야 하지만, 닫힐 때는 이미 볼 일이 끝나서 늘어지면 답답하다.
 */
const EXIT_MS = 220

/**
 * 시트가 미끄러지는 곡선.
 *
 * ease 는 끝에서 흐물거려 "툭" 떨어지는 느낌이 든다. 처음에 빠르게 밀고
 * 끝에서 부드럽게 멎는 곡선이라야 손으로 내린 것처럼 보인다.
 */
const SLIDE_CURVE = 'cubic-bezier(0.32, 0.72, 0, 1)'

/**
 * 커버를 지정하지 않은 리스트에 쓰는 기본 이미지.
 *
 * default-category-cover-bg.png는 왼쪽 정사각(수박)과 오른쪽 접시가 한 장에 붙어 있다.
 * 여기서는 접시 없이 정사각만 보여야 하므로, 배경 이미지로 깔고 왼쪽 정사각 영역만
 * 잘라 쓴다. 좌표는 PlateCoverImage가 쓰는 값과 동일하다(원본 169.34×108.09 기준,
 * 정사각은 left 4 / top 5 / 한 변 97).
 */
const DEFAULT_COVER_STYLE: React.CSSProperties = {
  backgroundImage: "url('/images/default-category-cover-bg.png?v=5')",
  backgroundSize: `${(169.34 / 97) * COVER_SIZE}px ${(108.09 / 97) * COVER_SIZE}px`,
  backgroundPosition: `${(-4 / 97) * COVER_SIZE}px ${(-5 / 97) * COVER_SIZE}px`,
  backgroundRepeat: 'no-repeat',
}

export interface PlatelistPickerSheetProps {
  isOpen: boolean
  onClose: () => void
  /**
   * 핸들 아래 첫 칸 — "무엇을 담는가". 여기만 시트마다 다르다.
   * 저장 시트는 장소 한 곳과 북마크 아이콘, 담기 시트는 "외 N곳" 요약.
   *
   * requestClose 를 받는다. 머리말에서 한 일로 시트를 닫아야 할 때
   * (저장 해제 등) onClose 를 직접 부르면 내려가는 동작 없이 사라지므로,
   * 반드시 이걸 쓴다. delayMs 를 주면 그만큼 기다렸다 내려간다.
   *
   * toastAfter 를 주면 **다 내려간 뒤에** 그 말을 띄운다. 토스트는 화면
   * 아래에서 올라오는데 시트가 아직 거기 있으면 가려진다.
   */
  header: (api: {
    requestClose: (delayMs?: number, toastAfter?: string) => void
  }) => React.ReactNode
  categories: BookmarkCategory[]
  /** 체크(✓)로 그릴 리스트 id. 나머지는 ⊕ */
  checkedIds: Set<string>
  isLoading: boolean
  onToggle: (category: BookmarkCategory) => void
  /** 제목 행 오른쪽 "추가"(리스트 새로 만들기) 를 눌렀을 때 */
  onCreate: () => void
}

/**
 * 플레이트리스트를 고르는 시트의 껍데기.
 *
 * 앱 안에서 "장소를 저장한 뒤 어느 리스트에 넣을지" 묻는 화면은 전부 이걸 쓴다
 * — 장소 상세·지도의 저장 시트(PlaceSaveSheet), 인스타/지도 링크 공유와 가입
 * 직후 불러오기의 담기 시트(SaveDestinationSheet).
 *
 * 왜 한 조각으로 뺐나
 * ------------------
 * 전에는 같은 생김새를 두 파일이 각자 그렸다. 행 높이·커버 좌표·체크 아이콘이
 * 모두 복사본이라, 한쪽만 고치면 소리 없이 어긋났다. 실제로 드롭다운으로 고르던
 * 시절의 담기 시트는 같은 앱처럼 보이지도 않았다.
 *
 * 공통인 것(행·목록·"추가" 버튼·드래그·배경 스크롤 막기)은 전부 여기
 * 있고, 다른 것은 머리말 하나뿐이라 header 로 받는다. 담고 빼는 규칙은 시트마다
 * 기준이 달라(내 저장 vs 여러 장소의 membership) 바깥에 남겨둔다.
 */
const PlatelistPickerSheet: React.FC<PlatelistPickerSheetProps> = ({
  isOpen,
  onClose,
  header,
  categories,
  checkedIds,
  isLoading,
  onToggle,
  onCreate,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<number | null>(null)
  const exitTimer = useRef<number | null>(null)
  /**
   * 이번에 새로 담은 리스트. id → 이름.
   *
   * 누를 때마다 토스트를 띄우지 않고 여기 적어 두었다가 닫힌 뒤에 한 번만
   * 말한다. 두 곳에 담으면 전에는 토스트가 두 개 겹쳤고, 담았다가 도로
   * 빼면 "담았어요" 만 남아 거짓말이 됐다. 뺄 때 지우므로 남는 것은 실제로
   * 들어간 것뿐이다.
   */
  const addedNames = useRef<Map<string, string>>(new Map())
  /** 머리말이 따로 부탁한 말. 있으면 이것만 띄운다 */
  const headerMessage = useRef<string | null>(null)
  /**
   * 지금 체크된 것들. 닫힐 때 토스트를 만들려고 들고 있는다.
   *
   * 닫는 일은 타이머 안에서 일어나므로, 그 안에서 보는 props 는 예약하던
   * 순간의 것이다. 그 사이에 서버가 실패해 체크가 풀렸을 수 있어 ref 로
   * 현재 값을 따로 본다 — 안 그러면 담기지도 않은 것을 담았다고 한다.
   */
  const latestChecked = useRef(checkedIds)
  useEffect(() => {
    latestChecked.current = checkedIds
  }, [checkedIds])
  /** 올라오는 동작이 끝났나 — 처음 한 프레임은 화면 밖에 둬야 미끄러진다 */
  const [entered, setEntered] = useState(false)
  /** 내려가는 중. 다 내려간 뒤에 onClose 를 부른다 */
  const [closing, setClosing] = useState(false)

  const cancelAutoClose = useCallback(() => {
    if (closeTimer.current === null) return
    window.clearTimeout(closeTimer.current)
    closeTimer.current = null
  }, [])

  /**
   * 다 내려간 뒤에 한 번 말한다.
   *
   * 토스트는 화면 맨 아래에서 올라온다. 시트가 아직 거기 있으면 그 뒤에
   * 가려서 안 보인다 — 닫히는 것을 기다렸다 띄워야 한다.
   */
  const flushToast = () => {
    const message = headerMessage.current
    headerMessage.current = null
    const added = Array.from(addedNames.current.entries())
      .filter(([id]) => latestChecked.current.has(id))
      .map(([, name]) => name)
    addedNames.current = new Map()

    if (message !== null) {
      toastUtil.success(message)

      return
    }
    if (added.length === 1) {
      toastUtil.success(`${added[0]}에 담았어요`)
    } else if (added.length > 1) {
      toastUtil.success(`플레이트리스트 ${added.length}개에 담았어요`)
    }
  }

  /**
   * 시트를 내려보낸다.
   *
   * onClose 를 바로 부르면 화면에서 그냥 사라진다 — 띄운 쪽이 그 순간
   * 렌더를 멈추기 때문이다. 그래서 먼저 아래로 밀어내고, 다 내려간 뒤에
   * 알린다. 인스타처럼 닫으면서 다른 화면으로 넘어가는 경우에도 이 순서라야
   * 시트가 내려가는 것이 보인다.
   */
  const requestClose = useCallback(
    (delayMs = 0, toastAfter?: string) => {
      cancelAutoClose()
      if (exitTimer.current !== null) return // 이미 내려가는 중
      if (toastAfter !== undefined) {
        headerMessage.current = toastAfter
      }

      const slideOut = () => {
        setClosing(true)
        exitTimer.current = window.setTimeout(() => {
          exitTimer.current = null
          flushToast()
          onClose()
        }, EXIT_MS)
      }

      if (delayMs > 0) {
        exitTimer.current = window.setTimeout(() => {
          exitTimer.current = null
          slideOut()
        }, delayMs)
        return
      }
      slideOut()
    },
    // flushToast 는 ref 만 읽어서 매 렌더 같은 일을 한다 — 의존성에 넣으면
    // 렌더마다 requestClose 가 새로 만들어져 예약해 둔 타이머가 헛돈다
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cancelAutoClose, onClose]
  )

  const armAutoClose = useCallback(() => {
    cancelAutoClose()
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null
      requestClose()
    }, AUTO_CLOSE_MS)
  }, [cancelAutoClose, requestClose])

  // 열릴 때마다 처음 상태로. 화면 밖에서 시작해 다음 프레임에 올라온다 —
  // 같은 프레임에 두 값을 주면 브라우저가 중간을 그리지 않아 그냥 나타난다.
  useEffect(() => {
    if (!isOpen) {
      setEntered(false)
      setClosing(false)
      return
    }
    addedNames.current = new Map()
    headerMessage.current = null
    const id = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(id)
  }, [isOpen])

  // 시트가 닫히거나 화면에서 사라지면 예약을 지운다 — 안 그러면 이미 닫힌
  // 시트의 타이머가 뒤늦게 터져 멀쩡히 열린 다음 시트를 닫는다.
  useEffect(() => {
    const clearAll = () => {
      cancelAutoClose()
      if (exitTimer.current !== null) {
        window.clearTimeout(exitTimer.current)
        exitTimer.current = null
      }
    }
    if (!isOpen) clearAll()
    return clearAll
  }, [isOpen, cancelAutoClose])

  // 시트가 열려 있는 동안 뒤 화면이 스크롤되지 않게 한다.
  // body에 overflow:hidden만 걸면 실제 스크롤 컨테이너가 안쪽 div인 화면
  // (피드·검색 등)에서는 그대로 밀린다. 시트 밖에서 시작한 스크롤 제스처를
  // 직접 막아야 어느 화면에서든 배경이 고정된다.
  useEffect(() => {
    if (!isOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const isInsideSheet = (target: EventTarget | null) =>
      target instanceof Node && sheetRef.current?.contains(target) === true

    const blockOutside = (e: TouchEvent | WheelEvent) => {
      if (!isInsideSheet(e.target)) e.preventDefault()
    }

    document.addEventListener('touchmove', blockOutside, { passive: false })
    document.addEventListener('wheel', blockOutside, { passive: false })

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('touchmove', blockOutside)
      document.removeEventListener('wheel', blockOutside)
    }
  }, [isOpen])

  const { handleProps, sheetStyle, dragging } = useSheetDragDismiss({
    onClose: requestClose,
    enabled: isOpen,
  })

  if (!isOpen) return null

  // 올라오기 전과 내려갈 때는 화면 밖(자기 높이만큼 아래). 그 사이에는
  // 드래그가 준 값을 그대로 쓴다 — 끌다가 놓으면 끌던 자리에서 이어 내려간다.
  const offscreen = closing || !entered
  const panelStyle: React.CSSProperties = {
    transform: offscreen ? 'translateY(100%)' : sheetStyle.transform,
    transition: dragging
      ? 'none'
      : `transform ${closing ? EXIT_MS : ENTER_MS}ms ${SLIDE_CURVE}`,
  }

  const stopBubbling = (e: React.SyntheticEvent) => e.stopPropagation()

  // 포털은 DOM상 body 아래지만 React 이벤트는 "React 트리"를 따라 버블링한다.
  // 그대로 두면 시트 안의 탭이 이 시트를 띄운 지도 바텀시트나 장소 카드의
  // onClick까지 올라가 뒤 화면이 같이 닫히거나 이동해버린다.
  return createPortal(
    <div
      ref={sheetRef}
      data-no-pull-refresh
      onClick={stopBubbling}
      onMouseDown={stopBubbling}
      onPointerDown={(e) => {
        cancelAutoClose()
        stopBubbling(e)
      }}
      onTouchStart={stopBubbling}
      onTouchEnd={stopBubbling}
    >
      <div
        className="fixed inset-0 bg-black z-[1055]"
        style={{
          opacity: offscreen ? 0 : 0.4,
          transition: `opacity ${closing ? EXIT_MS : ENTER_MS}ms ease`,
        }}
        onClick={() => requestClose()}
      />

      <div className="fixed bottom-0 left-0 right-0 z-[1060] flex justify-center">
        <div className="app-container flex flex-col" style={panelStyle}>
          <div
            className="bg-ivoryKey rounded-t-30px shadow-lg overflow-hidden flex flex-col w-full"
            style={{ maxHeight: `${Math.floor(window.innerHeight * 0.85)}px` }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 드래그 핸들 */}
            <div className="flex flex-col w-full" {...handleProps}>
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto mt-16px mb-16px" />
            </div>

            {/* 머리말 — 무엇을 담는가 */}
            {header({ requestClose })}

            {/* divider — 지도 장소 바텀시트와 동일 */}
            <div className="w-full h-1px">
              <div className="w-full h-full bg-divider-divider-1" />
            </div>

            {/* 제목 행 — 오른쪽에 만들기 */}
            <div className="w-full flex items-center justify-between px-20px pt-20px pb-8px">
              <div className="font-pretendard font-bold text-16px leading-125% text-brown-80%-main">
                플레이트리스트
              </div>
              <button
                type="button"
                onClick={onCreate}
                className="font-pretendard font-bold text-16px leading-125% text-brown-80%-main shrink-0"
              >
                추가
              </button>
            </div>

            <div
              className="overflow-y-auto scrollbar-hide"
              style={{
                // 리스트 끝까지 스크롤해도 뒤 화면으로 스크롤이 넘어가지 않게
                overscrollBehavior: 'contain',
                maxHeight: LIST_MAX_HEIGHT,
                // 로딩 중에도 4행 높이를 미리 잡아둔다. 안 그러면 '불러오는 중' 한 줄
                // 높이로 열렸다가 데이터가 오면서 시트가 한 번 확 늘어난다.
                // 비어 있을 때도 같은 높이를 잡는다. 안 그러면 '불러오는 중'
                // 에서 한 줄로 시트가 확 줄었다가, 리스트를 만들면 또 늘어난다.
                ...(isLoading || categories.length === 0
                  ? { height: LIST_MAX_HEIGHT }
                  : {}),
              }}
            >
              {isLoading ? (
                <div className="h-full flex items-center justify-center px-20px font-pretendard text-14px text-[rgba(75,54,33,0.6)] empty-state-center">
                  불러오는 중...
                </div>
              ) : categories.length === 0 ? (
                <div className="h-full flex items-center justify-center px-20px font-pretendard text-14px text-[rgba(75,54,33,0.6)] empty-state-center">
                  플레이트리스트가 없습니다
                </div>
              ) : (
                categories.map((category) => {
                  const isChecked = checkedIds.has(category.id)

                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => {
                        if (isChecked) {
                          addedNames.current.delete(category.id)
                        } else {
                          addedNames.current.set(category.id, category.name)
                        }
                        onToggle(category)
                        armAutoClose()
                      }}
                      className="w-full flex items-center px-20px py-12px"
                    >
                      <div
                        className="shrink-0 rounded-8px overflow-hidden"
                        style={{
                          width: COVER_SIZE,
                          height: COVER_SIZE,
                          ...(category.thumbnail_image
                            ? {}
                            : DEFAULT_COVER_STYLE),
                        }}
                      >
                        {category.thumbnail_image && (
                          <img
                            src={category.thumbnail_image}
                            alt={category.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>

                      <div className="flex-1 min-w-0 text-left pl-12px">
                        <div className="font-pretendard font-bold text-14px leading-125% text-brown-80%-main truncate">
                          {category.name}
                        </div>
                        <div className="font-pretendard text-12px leading-125% text-[rgba(75,54,33,0.6)]">
                          {category.is_public ? '공개' : '비공개'}
                        </div>
                      </div>

                      <img
                        src={
                          isChecked
                            ? '/images/checkbox-bold-filled.png'
                            : '/images/bold-plus-icon.png'
                        }
                        alt={isChecked ? '담김' : '담기'}
                        className="w-26px h-26px shrink-0"
                      />
                    </button>
                  )
                })
              )}
            </div>

            {/*
              완료 버튼은 두지 않는다.

              저장은 이 시트를 열기 전에 이미 끝났고, 리스트는 누르는 즉시
              들어가고 빠진다. 완료를 두면 같은 일을 두 가지 방식으로 하게 된다.
            */}
            <div className="w-full pb-32px" />
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default PlatelistPickerSheet
